import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../lib/api";

/**
 * Server-authoritative admin gate hook
 * Calls GET /api/admin/selftest to check if the current user is an admin
 * 
 * @returns {{ loading: boolean, isAdmin: boolean, userId: string | null }}
 */
export function useAdminGate() {
  const { user, authReady, getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function checkAdmin() {
      // If not authenticated, immediately return not admin
      if (!authReady || !user) {
        if (!cancelled) {
          setIsAdmin(false);
          setUserId(null);
          setLoading(false);
        }
        return;
      }

      try {
        // Use apiFetch to ensure Authorization header is included
        const response = await apiFetch('/admin/selftest', { method: 'GET' }, getToken);

        if (!cancelled) {
          // apiFetch now returns parsed data directly (not a Response object)
          if (response && response.ok) {
            setIsAdmin(response.isAdmin === true);
            setUserId(response.userId || null);
          } else {
            // Any non-OK response means not admin
            setIsAdmin(false);
            setUserId(null);
          }
        }
      } catch (error) {
        console.error('Admin check failed:', error);
        if (!cancelled) {
          setIsAdmin(false);
          setUserId(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    checkAdmin();

    // Cleanup function to prevent state updates after unmount
    return () => {
      cancelled = true;
    };
  }, [user, authReady, getToken]);

  return { loading, isAdmin, userId };
}
